/* The record of the INBOX (docs/design/inbox.md §6; round 089 cards 03,
   04, 15, 24 and 27; the fold of the Impeccable comp B). The markup is
   `ui/inbox/record.ts`, `rows.ts` and `open.ts`.

   Every rule reads the tokens of gen/tokens.css and stands under #ui,
   because `#ui button` sets the face of every chrome button and an id
   outranks a class. A row is a button, so its rules state the whole face.

   One type, one shape (build plan §1): every row is two lines in 44 px,
   with the bucket icon in a 24 px gutter, the type word in a column of 10
   characters, the subject, the stamp, and the figure strip of fixed
   cells under them. A bucket shows by the shape of its icon and by its
   ink; no bucket takes the gold. An unread row carries an accent rule at
   its left edge, its icon in the bucket ink and its subject in the hero
   ink; a read row takes the faint icon and type word and dims its
   figures (audit P6). */

@import '../../gen/tokens.css';

/* --- the bucket inks (089 · 01) --- */
#ui .b-loss { --bk: var(--bad); }
#ui .b-war { --bk: var(--text); }
#ui .b-hold { --bk: var(--amber); }
#ui .b-dive { --bk: var(--isotopes); }
#ui .b-trade { --bk: var(--text); }
#ui .b-world { --bk: var(--dim); }

/* --- the head: INBOX, the views with the unread count, MARK ALL READ ---
   One row on a telephone of 393 px and in the desktop dock, in both
   languages (inbox.md §6): the press stands at the right end. The row
   wraps only on a chrome narrower than those two, so no press leaves the
   sheet. */
#ui .ib-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--s-2) var(--s-3);
  min-height: var(--target-finger);
  padding: var(--s-1) 0;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.14);
}
#ui .ib-t {
  margin: 0;
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  color: var(--accent);
}
#ui .ib-head .ib-mark { flex: none; margin-left: auto; }

/* --- the filter row: ALL and the six buckets with their counts. It stays
   a row of buttons, the one exception to the select box of a filter of
   seven (R2038) --- */
#ui .ib-fr {
  display: flex;
  gap: 3px;
  padding: var(--s-2) 0;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.1);
}
#ui .ib-fc {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--s-1);
  min-height: var(--target-mouse);
  margin: 0;
  padding: 0 2px;
  border: 1px solid rgb(var(--accent-rgb) / 0.2);
  border-radius: var(--r-button);
  background: transparent;
  color: var(--dim);
  font: var(--t-cap);
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
}
#ui .ib-fc .ib-ic { color: var(--bk); }
#ui .ib-fc i { font-style: normal; font-variant-numeric: tabular-nums; }
#ui .ib-fc b { font-weight: 500; overflow: hidden; text-overflow: ellipsis; }
#ui .ib-fc.none, #ui .ib-fc.none .ib-ic { color: var(--faint); }
#ui .ib-fc.on {
  flex-grow: 2;
  background: rgb(var(--accent-rgb) / 0.18);
  border-color: rgb(var(--accent-rgb) / 0.6);
  color: var(--accent);
  box-shadow: inset 0 -2px 0 var(--accent);
}
/* WAR · REPORTS as a filter (089 · 08): the line that names it. */
#ui .ib-only {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-2) 0;
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  color: var(--accent);
}
#ui .ib-only > span { flex: 1; min-width: 0; }
#ui .ib-only .ib-p { flex: none; }
#ui .ib-empty { padding: var(--s-4) 0; font: var(--t-small); color: var(--dim); }

/* --- the row: two lines in 44 px --- */
#ui .ib-rows { display: flex; flex-direction: column; min-width: 0; }
#ui .ib-dl { display: flex; flex-direction: column; min-width: 0; }
/* A day head (audit P10, R3136): the word of the day, then a hairline,
   in the form of the day break of NEEDS YOU. */
#ui .ib-day {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  height: var(--s-6);
  margin: 0;
  color: var(--dim);
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  white-space: nowrap;
}
#ui .ib-day::after { content: ""; flex: 1; height: 1px; background: rgb(var(--accent-rgb) / 0.1); }
#ui .ib-row {
  position: relative;
  display: grid;
  grid-template-columns: 24px 80px minmax(0, 1fr) auto;
  grid-template-rows: 18px 16px;
  align-items: center;
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  margin: 0;
  padding: 5px 0 4px;
  border: 0;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.08);
  border-radius: 0;
  background: transparent;
  color: var(--text);
  font: var(--t-body);
  text-align: left;
  flex: none;
  min-width: 0;
}
/* The unread mark of line 1: the accent tick at the left edge of its
   host, which stands `position: relative` (089 · 02). Its word is for a
   screen reader alone. */
#ui .ib-u {
  position: absolute;
  left: -1px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--accent);
}
#ui .ib-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
#ui .ib-row.on { background: rgb(var(--accent-rgb) / 0.08); }
#ui .ib-g {
  grid-row: 1 / 3;
  grid-column: 1;
  align-self: stretch;
  display: grid;
  grid-template-rows: 18px 16px;
  justify-items: center;
  align-items: center;
  color: var(--bk);
}
#ui .ib-g > .ib-ic { grid-row: 1; }
/* The count of a fold stands in the gutter (089 · 15 a). */
#ui .ib-n {
  grid-row: 2;
  font: var(--t-cap);
  font-style: normal;
  letter-spacing: 0;
  color: var(--text);
}
#ui .ib-w {
  grid-row: 1;
  grid-column: 2;
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
}
#ui .ib-s {
  grid-row: 1;
  grid-column: 3;
  min-width: 0;
  padding-right: var(--s-3);
  color: var(--hero-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
#ui .ib-st {
  grid-row: 1;
  grid-column: 4;
  padding-right: 2px;
  font: var(--t-small);
  color: var(--dim);
  white-space: nowrap;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
#ui .ib-f {
  grid-row: 2;
  grid-column: 2 / 5;
  display: flex;
  align-items: center;
  gap: var(--s-3);
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}
/* Line 1 in a host of its own (a NEEDS YOU item): the same parts on one
   line, the type word in its column of 10 characters. */
#ui .ib-l1 { display: flex; align-items: baseline; gap: var(--s-2); min-width: 0; }
#ui .ib-l1 .ib-w { flex: none; width: 80px; }
#ui .ib-l1 .ib-s { flex: 1 1 auto; padding-right: 0; }
#ui .ib-l1 .ib-st { flex: none; }
#ui .ib-row.rd .ib-g, #ui .ib-row.rd .ib-w { color: var(--faint); }
#ui .ib-row.rd .ib-s { color: var(--text); }
#ui .ib-row.rd .ib-f .ib-v, #ui .ib-row.rd .ib-f .ib-ct, #ui .ib-row.rd .ib-f .ib-cw { color: var(--dim); }
#ui .ib-row.rd .ib-f .ib-mk, #ui .ib-row.rd .ib-f .ib-cf, #ui .ib-row.rd .ib-f .ib-sq { opacity: 0.7; }

/* --- the cells of the figure strip --- */
#ui .ib-ic { display: inline-block; flex: none; vertical-align: -2px; }
#ui .ib-c {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: none;
  min-width: 0;
  white-space: nowrap;
  font: var(--t-small);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}
#ui .ib-c.wr { flex: 1 1 auto; }
#ui .ib-mk { display: inline-flex; width: 12px; justify-content: center; color: var(--dim); }
#ui .ib-mk svg.ri { width: 12px; height: 12px; }
#ui .ib-v { display: inline-block; text-align: right; font: var(--t-body); color: var(--text); }
#ui .ib-c small { font: var(--t-small); color: var(--dim); margin-left: 1px; }
#ui .ib-ct, #ui .ib-cw { overflow: hidden; text-overflow: ellipsis; }
#ui .ib-ct.ib-r { justify-content: flex-end; text-align: right; }
#ui .ib-cw > span { overflow: hidden; text-overflow: ellipsis; min-width: 0; }
#ui .ib-cf { width: 16px; justify-content: center; }
#ui .ib-cf svg.ri { width: 13px; height: 13px; }
/* The widths of the word file, in characters of the cell's own type. A
   faction cell holds its square beside the name. */
#ui .ib-cn.w5 .ib-v { width: 5ch; }
#ui .ib-cn.w6 .ib-v { width: 6ch; }
#ui .ib-cn.w7 .ib-v { width: 7ch; }
#ui .ib-cn.w8 .ib-v { width: 8ch; }
#ui .ib-cn.w9 .ib-v { width: 9ch; }
#ui .ib-cn.w10 .ib-v { width: 10ch; }
#ui .ib-ct.w2 { width: 2ch; }
#ui .ib-ct.w6 { width: 6ch; }
#ui .ib-ct.w7 { width: 7ch; }
#ui .ib-ct.w8 { width: 8ch; }
#ui .ib-ct.w9 { width: 9ch; }
#ui .ib-ct.w10 { width: 10ch; }
#ui .ib-ct.w12 { width: 12ch; }
#ui .ib-ct.w13 { width: 13ch; }
#ui .ib-ct.w14 { width: 14ch; }
#ui .ib-cw.w14 { width: calc(14ch + 12px); }
#ui .ib-ct.wr, #ui .ib-cw.wr { flex: 1 1 0; }
/* A zero reads as a faint dash; an absent mark draws hollow (§1). */
#ui .ib-z, #ui .ib-c0 .ib-v, #ui .ib-c0 small { color: var(--faint); }
#ui .ib-c0 .ib-mk { opacity: 0.4; }
#ui .ib-hollow, #ui svg.ib-hollow { color: var(--faint); opacity: 0.5; }
#ui .ib-hollow * { fill: none; }
#ui .ib-hollow [stroke="none"] { stroke: currentColor; stroke-width: 1.2; }
#ui .ib-bad, #ui .ib-cn.ib-bad .ib-v, #ui .ib-ct.ib-bad, #ui .ib-cf.ib-bad { color: var(--bad); }
#ui .ib-good, #ui .ib-cn.ib-good .ib-v, #ui .ib-ct.ib-good { color: var(--good); }
#ui .ib-dim, #ui .ib-cn.ib-dim .ib-v, #ui .ib-ct.ib-dim { color: var(--dim); }
#ui .ib-sq {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 1.5px;
  flex: none;
  margin-right: 5px;
  vertical-align: 1px;
}

/* --- the fold unfolded in place (comp B): one 22 px line a member --- */
#ui .ib-fold {
  margin: 0 0 var(--s-2) 24px;
  padding-left: var(--s-2);
  border-left: 1px solid rgb(var(--accent-rgb) / 0.14);
}
#ui .ib-fold.ib-pg { margin: var(--s-2) 0; }
#ui .ib-mem {
  display: grid;
  grid-template-columns: 5ch minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: var(--s-2);
  height: 22px;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.06);
  font: var(--t-small);
}
#ui .ib-mt { color: var(--dim); font-variant-numeric: tabular-nums; white-space: nowrap; }
#ui .ib-ms { min-width: 0; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
#ui .ib-mf { display: flex; gap: var(--s-2); }
#ui .ib-mf small { display: none; }
#ui .ib-ma { display: flex; gap: var(--s-1); }
#ui .ib-mp {
  height: 20px;
  margin: 0;
  padding: 0 var(--s-2);
  border: 1px solid rgb(var(--accent-rgb) / 0.34);
  border-radius: var(--r-bar);
  background: rgb(var(--deep-rgb) / 0.88);
  color: var(--accent);
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  white-space: nowrap;
}
#ui .ib-ffoot { display: flex; gap: var(--s-2); padding: var(--s-2) 0; }
#ui .ib-ffoot .ib-p { flex: none; }

/* --- the presses: the dark face of 087 · 03 ---
   The row wraps, so no press leaves a telephone (audit P4), and REPLACE
   stands its price on a line of its own. */
#ui .ib-acts { display: flex; flex-wrap: wrap; gap: var(--s-2); padding: var(--s-3) 0; }
#ui .ib-p {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 2px var(--s-2);
  flex: 1 1 auto;
  min-height: var(--target-mouse);
  margin: 0;
  padding: 0 var(--s-3);
  border: 1px solid rgb(var(--accent-rgb) / 0.34);
  border-radius: var(--r-button);
  background: rgb(var(--deep-rgb) / 0.88);
  color: var(--accent);
  font: var(--t-body);
  font-weight: 500;
  letter-spacing: 0.08em;
  white-space: nowrap;
}
#ui .ib-p.dk1 { flex-grow: 1.6; }
#ui .ib-p.ib-rep { flex-direction: column; padding: var(--s-1) var(--s-3); }
#ui .ib-p .ib-cost { font-weight: 400; letter-spacing: 0; }
#ui .ib-p:disabled {
  opacity: 1;
  color: var(--faint);
  border-color: rgb(var(--text-rgb) / 0.16);
  background: rgb(var(--deep-rgb) / 0.5);
}
#ui .ib-note { font: var(--t-small); color: var(--dim); padding-bottom: var(--s-2); }

/* --- the open message as a page (089 · 24 b) --- */
#ui .ib-page { display: flex; flex-direction: column; min-width: 0; }
#ui .ib-bar {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  min-height: var(--target-finger);
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.14);
}
#ui .ib-back {
  min-height: var(--target-mouse);
  margin: 0;
  padding: 0 var(--s-3);
  border: 1px solid rgb(var(--accent-rgb) / 0.34);
  border-radius: var(--r-button);
  background: rgb(var(--deep-rgb) / 0.88);
  color: var(--accent);
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
}
#ui .ib-path { font: var(--t-cap); letter-spacing: var(--t-cap-track); color: var(--dim); }
#ui .ib-oh { display: flex; align-items: flex-start; gap: var(--s-3); padding: var(--s-3) 0; }
#ui .ib-ohi { display: flex; padding-top: 1px; color: var(--bk); }
#ui .ib-ohw { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: var(--s-1); }
#ui .ib-ohk { display: flex; align-items: baseline; gap: var(--s-3); }
#ui .ib-ohk i {
  flex: 1;
  font: var(--t-cap);
  font-style: normal;
  letter-spacing: var(--t-cap-track);
  color: var(--dim);
  white-space: nowrap;
}
#ui .ib-ohs { font: var(--t-small); color: var(--dim); white-space: nowrap; font-variant-numeric: tabular-nums; }
#ui .ib-ohw b { font: var(--t-title); letter-spacing: var(--t-title-track); color: var(--hero-ink); overflow-wrap: anywhere; }
/* The figure strip at the next type step: the cells of line 2, freed
   from their widths and wrapped. */
#ui .ib-ostrip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--s-2) var(--s-4);
  padding: var(--s-3) var(--s-2);
  background: rgb(var(--sheet-rgb));
  border-block: 1px solid rgb(var(--accent-rgb) / 0.12);
}
#ui .ib-ostrip .ib-c { font: var(--t-body); width: auto; }
#ui .ib-ostrip .ib-c .ib-v { font: var(--t-figure); width: auto; color: var(--hero-ink); }
#ui .ib-ostrip .ib-ct, #ui .ib-ostrip .ib-cw { overflow: visible; white-space: normal; }
#ui .ib-ob { padding: var(--s-3) 0 var(--s-1); }
#ui .ib-ob p { margin: 0 0 var(--s-2); font: var(--t-body); color: var(--text); }
#ui .ib-fig { padding: var(--s-2) 0; }
#ui .ib-hexmap path { fill: rgb(var(--bad-rgb) / 0.18); stroke: var(--bad); stroke-width: 1; stroke-dasharray: 2 1.5; }
#ui .ib-score { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: var(--s-2); }
#ui .ib-score span { display: flex; flex-direction: column; gap: 2px; }
#ui .ib-score b { font: var(--t-figure); color: var(--hero-ink); font-variant-numeric: tabular-nums; }
#ui .ib-score i { font: var(--t-cap); font-style: normal; letter-spacing: var(--t-cap-track); color: var(--dim); }
/* A table of 22 px lines; it scrolls in its own box, so a wide table
   never moves the page sideways on a telephone. */
#ui .ib-tw { overflow-x: auto; max-width: 100%; margin: var(--s-2) 0; }
#ui .ib-tt { margin-bottom: var(--s-1); font: var(--t-cap); letter-spacing: var(--t-cap-track); color: var(--dim); }
#ui .ib-tbl { border-collapse: collapse; width: 100%; font: var(--t-small); font-variant-numeric: tabular-nums; }
#ui .ib-tbl th {
  height: 22px;
  padding: 0 6px;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.2);
  font: var(--t-cap);
  letter-spacing: 0.08em;
  color: var(--dim);
  text-align: left;
  white-space: nowrap;
}
#ui .ib-tbl td {
  height: 22px;
  max-width: 160px;
  padding: 0 6px;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.06);
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
#ui .ib-tbl th:first-child, #ui .ib-tbl td:first-child { padding-left: 2px; }
#ui .ib-tbl .r { text-align: right; }
/* The place line with GO TO. */
#ui .ib-op {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  min-height: var(--target-mouse);
  margin-top: var(--s-1);
  padding: var(--s-1) 0;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
  color: var(--dim);
}
#ui .ib-opn { flex: 1; min-width: 0; display: flex; flex-direction: column; }
#ui .ib-opn b { font: var(--t-body); font-weight: 400; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
#ui .ib-opn i { font: var(--t-small); font-style: normal; color: var(--dim); }
#ui .ib-op .ib-p { flex: none; }
#ui .ib-exp { padding: var(--s-2) 0; font: var(--t-small); color: var(--dim); }

@media (hover: hover) {
  #ui .ib-row:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.06); color: var(--text); }
  #ui .ib-p:hover:not(:disabled), #ui .ib-mp:hover:not(:disabled), #ui .ib-back:hover:not(:disabled),
  #ui .ib-fc:hover:not(.on) {
    background: rgb(var(--accent-rgb) / 0.14);
    color: var(--hover-ink);
  }
}

/* A finger needs the touch floor (audit P14): every press of the record
   grows to it on a coarse pointer, a member line too. */
@media (pointer: coarse) {
  #ui .ib-fc, #ui .ib-p, #ui .ib-back { min-height: var(--target-finger); }
  #ui .ib-mem { height: var(--target-finger); }
  #ui .ib-mp { height: calc(var(--target-finger) - var(--s-2)); }
}
